import { useEffect, useRef, useState } from "react";
import { getCandidateByID } from "../../services/candidateService";
import { Candidate } from "../../interfaces/candidate";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { Order } from "../../interfaces/order";
import {
  getOrderBySuranID,
  sendNotifySuranEmailOrder,
  updateOrderPaymentStatusBySuranID,
} from "../../services/orderService";
import {
  createCandidatePaymentSuran,
  getCandidatePayment,
  processPaymentIntent,
  updateCandidatePaymentSuran,
} from "../../services/paymentService";
import PaymentForm from "../../components/PaymentForm";
import { SimpleClient } from "../../interfaces/client";
import { getSimpleClient } from "../../services/clientService";
import { sendNotifyClientEmail } from "../../services/emailService";
import { NotificationType } from "../../interfaces/clientNotification";

const SuranPayment = () => {
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order>();
  const [client, setClient] = useState<SimpleClient>();
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const { suranID } = useParams();

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const key =
    window.location.hostname === "localhost"
      ? process.env.REACT_APP_STRIPE_TEST_KEY
      : process.env.REACT_APP_STRIPE_KEY;
  const stripePromise = loadStripe(key || "");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const stripe = useStripe();

  const emailSentRef = useRef(false);

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const suranIDString = suranID?.toString();
        if (suranIDString) {
          const order = await getOrderBySuranID(suranIDString);
          setOrder(order);

          if (order.paymentReceived === true) {
            navigate("/suran/thank-you", {});
          }

          const candidate = await getCandidateByID(order.candidateID);
          setCandidate(candidate);

          const client = await getSimpleClient(order.clientID);
          setClient(client);
        }
      } catch (error) {
        console.error("Error loading order or candidate:", error);
        navigate("/error", {});
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [suranID, navigate]);

  const handlePayment = async () => {
    if (order && order.requiresCandidatePayment && !order.paymentReceived) {
      try {
        if (candidate) {
          const paymentRecord = await createCandidatePaymentSuran({
            paymentID: 0,
            orderID: order.orderID,
            paymentIntentID: "xxxxxx",
            paymentMethod: "card",
            amount: 175,
            currency: "usd",
            paymentSuccess: false,
            paymentDate: new Date(),
          });
          const response = await processPaymentIntent(
            175,
            paymentRecord.paymentID,
            order.candidateEmail ? order.candidateEmail : "",
          );
          localStorage.setItem("paymentID", paymentRecord.paymentID.toString());
          setClientSecret(response.clientSecret);
          setShowPaymentModal(true);
        }
      } catch (error: any) {
        console.log(error);
      }
      return;
    } else {
      navigate("/error", {});
      console.error("order not found.");
    }
  };

  //check for success or failure of payment
  useEffect(() => {
    if (!stripe || !suranID) return;

    const clientSecret = new URLSearchParams(location.search).get(
      "payment_intent_client_secret",
    );

    const orderIDStr = new URLSearchParams(location.search).get("orderID");
    const orderID = orderIDStr ? parseInt(orderIDStr, 10) : NaN;
    if (!clientSecret) return;

    stripe
      .retrievePaymentIntent(clientSecret)
      .then(async ({ paymentIntent }) => {
        if (!paymentIntent) return;
        if (paymentIntent.status === "succeeded" && orderID) {
          const updatedOrder = await updateOrderPaymentStatusBySuranID(
            suranID,
            orderID,
            true,
          );
          setOrder((prev) =>
            prev ? { ...prev, ...updatedOrder } : updatedOrder,
          );

          try {
            const paymentIDStr = localStorage.getItem("paymentID");
            const paymentID = paymentIDStr ? parseInt(paymentIDStr, 10) : NaN;
            const existingCandidatePayment =
              await getCandidatePayment(paymentID);
            if (existingCandidatePayment) {
              const updatedCandidatePayment = {
                ...existingCandidatePayment,
                PaymentIntentID: paymentIntent.id,
                PaymentSuccess: true,
                PaymentDate: new Date(),
              };
              await updateCandidatePaymentSuran(updatedCandidatePayment);
            }
          } catch (error) {
            console.error("Error retrieving candidate payment record:", error);
          }
          //notify suran
          if (!emailSentRef.current && order && client) {
            emailSentRef.current = true;
            await sendNotifySuranEmailOrder(order, client);
            await sendNotifyClientEmail({
              orderId: order.orderID,
              type: NotificationType.Order,
            });
          }

          navigate("/suran/thank-you");
        } else {
          navigate("/error", {});
        }
      });
  }, [stripe, location.search, order, client, navigate, suranID]);

  return (
    <div className="content-container">
      <div className="form-header-container">
        <div className="page-items-container"></div>
      </div>

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="text-form-header">Billing Information</div>
          <div className="divider"></div>

          <div className="text-body">
            The denominational leadership of the {client?.clientName} require
            that you prepay via credit card for your background check. They have
            negotiated a fixed-fee of $175.00, which is a substantial discount
            from our customary fee.
          </div>
          <br></br>
          <div className="data-container">
            <div className="text-body">
              Please press the "Submit Payment" button and then enter your
              credit card information. After you have entered all of your
              information, click the "Pay $175" button and your payment will be
              submitted.
            </div>

            <div className="order-card final-card" style={{ width: "100%" }}>
              <div>
                <div className="form-name">
                  <i
                    className="bi bi-send-exclamation"
                    style={{
                      color: "grey",
                      paddingRight: "10px",
                    }}
                  ></i>
                  PAYMENT FOR SUBMISSION
                </div>
                <div className="client-name">Please pay to proceed.</div>
              </div>
              <div className="form-status">
                <button
                  className="smbtn btn-green"
                  onClick={() => handlePayment()}
                >
                  SUBMIT PAYMENT
                </button>
              </div>
            </div>
          </div>

          {showPaymentModal && clientSecret && order && (
            <Elements options={{ clientSecret }} stripe={stripePromise}>
              <PaymentForm
                type="suran"
                amount={175}
                orderID={order.orderID}
                suranID={suranID}
                onClose={() => {
                  setShowPaymentModal(false);
                }}
              />
            </Elements>
          )}
        </>
      )}
    </div>
  );
};

export default SuranPayment;
